import {none, some} from '@danwang/option';
import {useAllAccountsQuery} from '@transactions/graphql';
import * as React from 'react';
import {AccountAvatar} from 'src/AccountAvatar';
import {AccountInlineDisplay} from 'src/AccountInlineDisplay';
import {isAccountClosed} from 'src/AccountsView/accountFilter';
import {FormButton} from 'src/FormButton';
import {renderOptionDefault} from 'src/SearchPopover/SearchBox';
import SearchInput from 'src/SearchPopover/SearchInput';
import SearchPopover from 'src/SearchPopover/SearchPopover';
import {useSearch} from 'src/useSearch';
import type {Option} from '@danwang/option';
import type {AccountListItemFragment} from '@transactions/graphql';
import type {RenderSelectionProps} from 'src/SearchPopover/SearchBox';
import type {RenderProps} from 'src/SearchPopover/SearchPopover';

type Props = {
  autoFocus?: boolean;
  /** Account ids to hide from the options (e.g. the account being moved from). */
  excludeIds?: string[];
  /**
   * Only offer accounts denominated in this currency (e.g. when moving a
   * raw transaction or creating its inverse — the server rejects
   * cross-currency destinations).
   */
  currency?: string;
  selectedAccount: Option<AccountListItemFragment>;
  onSelectAccount: (accountId: Option<AccountListItemFragment>) => void;
};

const AccountSelect = (props: Props) => {
  const {excludeIds, currency} = props;
  const {loading, data} = useAllAccountsQuery();
  // Every AccountSelect use picks a write destination (create, move,
  // import, credit target) — closed accounts accept nothing dated after
  // their close, so hide them rather than offer picks that will usually
  // fail. (Backdated writes into a closed account go through the account's
  // own pages, not this picker.)
  const accounts = (data?.allAccounts ?? []).filter(
    (account) =>
      !isAccountClosed(account) &&
      !excludeIds?.includes(account._id) &&
      (currency == null || account.currency === currency),
  );
  // The current selection stays searchable even when the filters above
  // exclude it (a closed or cross-currency account seeded by the caller):
  // Tab submits the first match, so the selection must be able to win that
  // slot or an untouched tab-through re-points to a different account.
  const searchable = props.selectedAccount
    .map((selected) =>
      accounts.some((account) => account._id === selected._id)
        ? accounts
        : [selected, ...accounts],
    )
    .getOrElse(accounts);
  const {search} = useSearch(searchable, ['name']);
  const handleSearch = (query: string): AccountListItemFragment[] | false => {
    if (loading) return false;
    const results = search(query);
    // The current selection ranks first among matches. Tab submits the
    // first result, and the popover opens seeded with the selection's name —
    // so with two same-named accounts, an untouched tab-through must land
    // back on the selected one, not whichever duplicate sorts first.
    return props.selectedAccount
      .map((selected) =>
        results.some((account) => account._id === selected._id)
          ? [
              selected,
              ...results.filter((account) => account._id !== selected._id),
            ]
          : results,
      )
      .getOrElse(results);
  };

  const renderButton = ({
    anchorRef,
    openPopover,
  }: RenderProps<HTMLButtonElement>) => {
    return (
      <FormButton
        ref={anchorRef}
        autoFocus={props.autoFocus}
        onClick={openPopover}
        onFocus={openPopover}
      >
        {props.selectedAccount
          .map((account) => (
            <div className="flex items-center gap-2">
              <AccountAvatar account={account} />
              <span className="truncate">{account.name}</span>
            </div>
          ))
          .getOrElse(
            <div className="text-muted-foreground text-left text-sm">
              Select account...
            </div>,
          )}
      </FormButton>
    );
  };

  const renderSelection = ({
    query,
    onChange,
    onKeyDown,
  }: RenderSelectionProps) => (
    <SearchInput
      autoFocus
      value={query}
      onChange={onChange}
      onKeyDown={onKeyDown}
      placeholder="Select account..."
    />
  );

  const renderOption = renderOptionDefault((account: AccountListItemFragment) => (
    <AccountInlineDisplay account={account} />
  ));

  const handleClear = () => props.onSelectAccount(none);
  const handleSubmit = (account: AccountListItemFragment) =>
    props.onSelectAccount(some(account));

  return (
    <SearchPopover
      initialQuery={props.selectedAccount
        .map((account) => account.name)
        .getOrElse('')}
      onClear={handleClear}
      onSearch={handleSearch}
      onSubmit={handleSubmit}
      renderButton={renderButton}
      renderSelection={renderSelection}
      renderOption={renderOption}
    />
  );
};

export default AccountSelect;
